import { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  Pressable,
  FlatList,
  ActivityIndicator,
} from 'react-native';
import { X, MessageSquare, Plus } from 'lucide-react-native';
import { theme } from '@/constants/theme';
import { conversationService, ConversationListItem } from '@/utils/conversationService';

interface ConversationsListModalProps {
  visible: boolean;
  onClose: () => void;
  onSelectConversation: (conversationId: string) => void;
  onNewConversation: () => void;
}

export function ConversationsListModal({
  visible,
  onClose,
  onSelectConversation,
  onNewConversation,
}: ConversationsListModalProps) {
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      loadConversations();
    }
  }, [visible]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const data = await conversationService.getAllConversations();
      setConversations(data);
    } catch (error) {
      console.error('[ConversationsListModal] Error loading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `לפני ${diffMins} דקות`;
    } else if (diffHours < 24) {
      return `לפני ${diffHours} שעות`;
    } else if (diffDays === 1) {
      return 'אתמול';
    } else if (diffDays < 7) {
      return `לפני ${diffDays} ימים`;
    } else {
      return date.toLocaleDateString('he-IL', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    }
  };

  const renderConversation = ({ item }: { item: ConversationListItem }) => (
    <Pressable
      style={({ pressed }) => [
        styles.conversationItem,
        pressed && styles.conversationItemPressed,
      ]}
      onPress={() => {
        onSelectConversation(item.id);
        onClose();
      }}
      accessibilityLabel={`שיחה מ-${formatDate(item.updated_at)}`}
      accessibilityRole="button"
    >
      <View style={styles.conversationIcon}>
        <MessageSquare size={20} color={theme.colors.primary} />
      </View>
      <View style={styles.conversationInfo}>
        <Text style={styles.conversationDate}>{formatDate(item.updated_at)}</Text>
        <Text style={styles.conversationId}>שיחה #{item.id.slice(0, 8)}</Text>
      </View>
    </Pressable>
  );

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <Text style={styles.title}>השיחות שלי</Text>
            <Pressable
              style={({ pressed }) => [
                styles.closeButton,
                pressed && styles.closeButtonPressed,
              ]}
              onPress={onClose}
              accessibilityLabel="סגור"
              accessibilityRole="button"
            >
              <X size={24} color={theme.colors.text} />
            </Pressable>
          </View>

          <Pressable
            style={({ pressed }) => [
              styles.newConversationButton,
              pressed && styles.newConversationButtonPressed,
            ]}
            onPress={() => {
              onNewConversation();
              onClose();
            }}
            accessibilityLabel="שיחה חדשה"
            accessibilityRole="button"
          >
            <Plus size={20} color={theme.colors.background} />
            <Text style={styles.newConversationText}>שיחה חדשה</Text>
          </Pressable>

          {loading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color={theme.colors.primary} />
            </View>
          ) : conversations.length === 0 ? (
            <View style={styles.emptyContainer}>
              <MessageSquare size={48} color={theme.colors.textLight} />
              <Text style={styles.emptyText}>אין שיחות קודמות</Text>
              <Text style={styles.emptySubtext}>התחל שיחה חדשה כדי להתחיל</Text>
            </View>
          ) : (
            <FlatList
              data={conversations}
              renderItem={renderConversation}
              keyExtractor={(item) => item.id}
              contentContainerStyle={styles.listContainer}
              showsVerticalScrollIndicator={false}
            />
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: theme.colors.background,
    borderTopLeftRadius: theme.spacing.lg,
    borderTopRightRadius: theme.spacing.lg,
    maxHeight: '80%',
    paddingBottom: theme.spacing.xl,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  title: {
    fontSize: 20,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
  },
  closeButton: {
    padding: theme.spacing.xs,
  },
  closeButtonPressed: {
    opacity: 0.7,
  },
  newConversationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xs,
    backgroundColor: theme.colors.primary,
    marginHorizontal: theme.spacing.md,
    marginTop: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.spacing.sm,
  },
  newConversationButtonPressed: {
    opacity: 0.8,
  },
  newConversationText: {
    fontSize: 16,
    fontFamily: theme.fonts.bold,
    color: theme.colors.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: theme.spacing.xl,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: theme.spacing.xl,
    gap: theme.spacing.sm,
  },
  emptyText: {
    fontSize: 18,
    fontFamily: theme.fonts.bold,
    color: theme.colors.text,
    marginTop: theme.spacing.md,
  },
  emptySubtext: {
    fontSize: 14,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
  },
  listContainer: {
    padding: theme.spacing.md,
    gap: theme.spacing.sm,
  },
  conversationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.spacing.sm,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  conversationItemPressed: {
    backgroundColor: theme.colors.border,
  },
  conversationIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: theme.colors.background,
    justifyContent: 'center',
    alignItems: 'center',
  },
  conversationInfo: {
    flex: 1,
    gap: theme.spacing.xs,
  },
  conversationDate: {
    fontSize: 16,
    fontFamily: theme.fonts.medium,
    color: theme.colors.text,
  },
  conversationId: {
    fontSize: 12,
    fontFamily: theme.fonts.regular,
    color: theme.colors.textLight,
  },
});
